import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { formatEventTimestamp } from '../../../utils/dateUtils';
import StatusChip from '../../../components/StatusChip';
import { getStatusColor } from '../../../utils/statusChipUtil';

/**
 * DeliveryLogsRow component renders a single delivery log row in the delivery logs list
 * @param {Object} deliveryLog - Delivery log object with id, http_status, webhook_url, finished_at
 * @param {boolean} isLast - Whether this is the last item in the list
 * @param {string} selectedDeliveryLogId - Currently selected delivery log ID for highlighting
 */
const DeliveryLogsRow = ({ deliveryLog, isLast, selectedDeliveryLogId }) => {
  const navigate = useNavigate();
  // Compare selectedDeliveryLogId (from URL) with id (converted to string to match URL param)
  const isSelected = selectedDeliveryLogId === String(deliveryLog.id);

  const handleClick = () => {
    navigate(`/delivery-logs/${deliveryLog.id}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  // Get HTTP status for status chip
  const httpStatus = deliveryLog.http_status;
  const statusColor = httpStatus ? getStatusColor(httpStatus) : undefined;
  const statusLabel = httpStatus ? String(httpStatus) : undefined;

  // Get event ID
  const eventId = deliveryLog.webhook_event_id;

  // Get timestamp
  const timestamp = deliveryLog.finished_at;

  return (
    <>
      <Box
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1.5,
          py: 1.25,
          cursor: 'pointer',
          borderRadius: '6px',
          border: '2px solid transparent',
          backgroundColor: 'transparent',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: isSelected ? 'transparent' : 'action.hover',
          },
          '&:focus': {
            borderColor: 'primary.main',
            outline: 'none',
          },
        }}
        tabIndex={0}
      >
        {/* Left section: Status chip and Event ID */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          {statusLabel && <StatusChip color={statusColor} label={statusLabel} size="small" />}
          {eventId && (
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 500,
                fontSize: '0.875rem',
                color: 'text.secondary',
              }}
            >
              {eventId}
            </Typography>
          )}
        </Box>

        {/* Right section: Time */}
        <Box sx={{ ml: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {timestamp && formatEventTimestamp(timestamp)}
          </Typography>
        </Box>
      </Box>
      {!isLast && <Divider />}
    </>
  );
};

export default DeliveryLogsRow;
